import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Section = ({ number, title, children }) => (
  <section className="border-t border-neutral-200 pt-7 mt-7">
    <div className="flex items-start gap-4">
      <span className="shrink-0 text-xs font-semibold tracking-wider text-[#8A6D1F] pt-1">
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-xl md:text-2xl font-serif text-neutral-950 mb-3" style={{ fontFamily: "Georgia, serif" }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  </section>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2.5 text-sm md:text-base text-neutral-700 leading-relaxed">
    {items.map((item, index) => (
      <li key={index} className="flex gap-3">
        <span className="text-[#A9843F] mt-0.5" aria-hidden="true">•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function AIConsentInformationPage() {
  const { t } = useTranslation();

  const purposes = ["visual", "documents", "conservation", "valuation"];
  const dataItems = ["images", "documents", "metadata", "condition", "valuation", "outputs"];
  const controlItems = ["onDemand", "review", "noSilentWrite", "permissions"];
  const limitItems = ["indicative", "noAuthentication", "noProfessionalReplacement", "uncertainty"];

  return (
    <main className="min-h-screen bg-[#FAFAF8]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 md:py-16">
        <div className="mb-8">
          <Link
            to="/profile"
            className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-950 mb-8"
          >
            ← {t("aiConsentInfoPage.backToProfile")}
          </Link>

          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8A6D1F] mb-3">
            {t("aiConsentInfoPage.eyebrow")}
          </p>
          <h1 className="text-3xl md:text-5xl font-serif text-neutral-950 leading-tight mb-5" style={{ fontFamily: "Georgia, serif" }}>
            {t("aiConsentInfoPage.title")}
          </h1>
          <p className="text-base md:text-lg text-neutral-700 leading-relaxed max-w-3xl">
            {t("aiConsentInfoPage.intro")}
          </p>
        </div>

        <div className="rounded-2xl border border-[#E7D9B4] bg-[#FBF7EC] p-5 md:p-6">
          <p className="font-semibold text-[#6B531A] mb-2">{t("aiConsentInfoPage.principle.title")}</p>
          <p className="text-sm md:text-base text-[#6B531A] leading-relaxed">
            {t("aiConsentInfoPage.principle.body")}
          </p>
        </div>

        <Section number="01" title={t("aiConsentInfoPage.access.title")}>
          <div className="space-y-3 text-sm md:text-base text-neutral-700 leading-relaxed">
            <p>{t("aiConsentInfoPage.access.body")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <p className="font-semibold text-neutral-900 mb-1">{t("aiConsentInfoPage.access.standardTitle")}</p>
                <p className="text-sm text-neutral-600">{t("aiConsentInfoPage.access.standardBody")}</p>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <p className="font-semibold text-neutral-900 mb-1">{t("aiConsentInfoPage.access.eligibleTitle")}</p>
                <p className="text-sm text-neutral-600">{t("aiConsentInfoPage.access.eligibleBody")}</p>
              </div>
            </div>
          </div>
        </Section>

        <Section number="02" title={t("aiConsentInfoPage.purposes.title")}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {purposes.map((key) => (
              <div key={key} className="rounded-xl border border-neutral-200 bg-white p-4 md:p-5">
                <h3 className="font-semibold text-neutral-950 mb-2">
                  {t(`aiConsentInfoPage.purposes.${key}.title`)}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {t(`aiConsentInfoPage.purposes.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </Section>

        <Section number="03" title={t("aiConsentInfoPage.data.title")}>
          <p className="text-sm md:text-base text-neutral-700 leading-relaxed mb-4">
            {t("aiConsentInfoPage.data.intro")}
          </p>
          <BulletList items={dataItems.map((key) => t(`aiConsentInfoPage.data.items.${key}`))} />
        </Section>

        <Section number="04" title={t("aiConsentInfoPage.control.title")}>
          <BulletList items={controlItems.map((key) => t(`aiConsentInfoPage.control.items.${key}`))} />
        </Section>

        <Section number="05" title={t("aiConsentInfoPage.limits.title")}>
          <BulletList items={limitItems.map((key) => t(`aiConsentInfoPage.limits.items.${key}`))} />
        </Section>

        <Section number="06" title={t("aiConsentInfoPage.statistics.title")}>
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 md:p-5 text-sm md:text-base text-blue-900 leading-relaxed">
            <p>{t("aiConsentInfoPage.statistics.body")}</p>
          </div>
        </Section>

        <Section number="07" title={t("aiConsentInfoPage.consent.title")}>
          <div className="space-y-3 text-sm md:text-base text-neutral-700 leading-relaxed">
            <p>{t("aiConsentInfoPage.consent.body")}</p>
            <p>{t("aiConsentInfoPage.consent.withdrawal")}</p>
            <p>{t("aiConsentInfoPage.consent.afterWithdrawal")}</p>
          </div>
        </Section>

        <Section number="08" title={t("aiConsentInfoPage.privacy.title")}>
          <div className="space-y-3 text-sm md:text-base text-neutral-700 leading-relaxed">
            <p>{t("aiConsentInfoPage.privacy.permissions")}</p>
            <p>{t("aiConsentInfoPage.privacy.noPublicity")}</p>
            <p>{t("aiConsentInfoPage.privacy.technicalDetails")}</p>
          </div>
        </Section>

        <div className="mt-10 rounded-2xl bg-neutral-950 text-white p-6 md:p-8">
          <h2 className="text-2xl font-serif mb-3" style={{ fontFamily: "Georgia, serif" }}>
            {t("aiConsentInfoPage.footer.title")}
          </h2>
          <p className="text-sm md:text-base text-neutral-300 leading-relaxed mb-5">
            {t("aiConsentInfoPage.footer.body")}
          </p>
          <Link
            to="/profile"
            className="inline-flex items-center justify-center rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-neutral-100"
          >
            {t("aiConsentInfoPage.footer.cta")}
          </Link>
        </div>
      </div>
    </main>
  );
}
